import { useState, useEffect, useRef } from 'react'
import {
  Alert,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  TextField,
  Box,
} from '@mui/material'
import { Download, Email } from '@mui/icons-material'
import { useStore } from '../store/useStore'
import { generatePDF, sendPDFByEmail } from '../utils/api'
import { DownloadModal } from './DownloadModal'

const EMAIL_STORAGE_KEY = 'lastUsedEmail'

type Toast = { severity: 'success' | 'error'; message: string } | null

export const ActionButtons = () => {
  const [emailDialogOpen, setEmailDialogOpen] = useState(false)
  const [downloadDialogOpen, setDownloadDialogOpen] = useState(false)
  const [email, setEmail] = useState('')
  const [filename, setFilename] = useState('')
  const [loading, setLoading] = useState(false)
  const [toast, setToast] = useState<Toast>(null)
  const filenameInputRef = useRef<HTMLInputElement>(null)

  const { sessionId, files } = useStore()
  const selectedFiles = files.filter((f) => f.selected)
  const fileIds = selectedFiles.length > 0
    ? selectedFiles.sort((a, b) => (a.order || 0) - (b.order || 0)).map((f) => f.id)
    : files.map((f) => f.id)

  useEffect(() => {
    const savedEmail = localStorage.getItem(EMAIL_STORAGE_KEY)
    if (savedEmail) {
      setEmail(savedEmail)
    }
  }, [])

  useEffect(() => {
    if (emailDialogOpen && filenameInputRef.current && localStorage.getItem(EMAIL_STORAGE_KEY)) {
      filenameInputRef.current.focus()
    }
  }, [emailDialogOpen])

  const handleDownload = async (filenameInput?: string) => {
    try {
      setLoading(true)
      const pdfBlob = await generatePDF(sessionId!, fileIds)
      const url = window.URL.createObjectURL(pdfBlob)
      const a = document.createElement('a')
      a.href = url
      const finalName = filenameInput ? `${filenameInput}.pdf` : 'whatsapp-images.pdf'
      a.download = finalName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
      setToast({ severity: 'success', message: `Downloaded ${finalName}` })
    } catch (error) {
      console.error('Error downloading PDF:', error)
      setToast({ severity: 'error', message: 'Could not generate the PDF. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  const handleEmailSend = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault()
    }

    if (!email) return

    const sentTo = email
    setEmailDialogOpen(false)
    setFilename('')

    try {
      setLoading(true)
      localStorage.setItem(EMAIL_STORAGE_KEY, email)

      await sendPDFByEmail(sessionId!, {
        to: email,
        files: fileIds,
        filename: filename || undefined,
      })
      setToast({ severity: 'success', message: `PDF sent to ${sentTo}` })
    } catch (error) {
      console.error('Error sending PDF via email:', error)
      setToast({ severity: 'error', message: 'Could not send the PDF. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleEmailSend()
    }
  }

  const ctaCount = selectedFiles.length || files.length
  const downloadLabel = selectedFiles.length > 0
    ? `Download PDF (${selectedFiles.length} selected)`
    : `Download PDF (all ${files.length})`
  const emailLabel = selectedFiles.length > 0
    ? `Email PDF (${selectedFiles.length} selected)`
    : `Email PDF (all ${files.length})`

  return (
    <>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={2}
        sx={{ mt: 4 }}
      >
        <Button
          variant="contained"
          startIcon={<Download />}
          onClick={() => setDownloadDialogOpen(true)}
          disabled={loading || ctaCount === 0}
          fullWidth
          sx={{
            bgcolor: 'primary.main',
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          {downloadLabel}
        </Button>
        <Button
          variant="outlined"
          startIcon={<Email />}
          onClick={() => setEmailDialogOpen(true)}
          disabled={loading || ctaCount === 0}
          fullWidth
        >
          {emailLabel}
        </Button>
      </Stack>

      <DownloadModal
        open={downloadDialogOpen}
        onClose={() => setDownloadDialogOpen(false)}
        onDownload={handleDownload}
      />

      <Dialog
        open={emailDialogOpen}
        onClose={() => {
          setEmailDialogOpen(false)
          setFilename('')
        }}
        maxWidth="sm"
        fullWidth
      >
        <form onSubmit={handleEmailSend}>
          <DialogTitle>Send PDF via Email</DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                autoFocus={!email}
                label="Email Address"
                type="email"
                fullWidth
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                onKeyDown={handleKeyDown}
              />
              <TextField
                inputRef={filenameInputRef}
                label="Filename (optional)"
                fullWidth
                value={filename}
                onChange={(e) => setFilename(e.target.value)}
                onKeyDown={handleKeyDown}
                InputProps={{
                  endAdornment: '.pdf',
                }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => {
                setEmailDialogOpen(false)
                setFilename('')
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={!email || loading}
            >
              Send
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? (
          <Alert
            severity={toast.severity}
            onClose={() => setToast(null)}
            sx={{ width: '100%' }}
            variant="filled"
          >
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </>
  )
}
